import { Component, OnDestroy, OnInit } from '@angular/core';
import { DbDocument, DocumentHelper, TemplateUtils } from 'fakturnia-shared';
import { DocumentsService } from 'shared/services/documents.service';
import { Utils } from 'fakturnia-shared';
import { CommonModule } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DataSynchronizerService } from 'shared/services/data-synchronization.service';
import { Subscription } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { DateTime } from 'luxon';
import { DirectionBadgeComponent } from '../direction-badge/direction-badge.component';

@Component({
  standalone: true,
  imports: [CommonModule, MatMenuModule, MatButtonModule, MatIconModule, MatTooltipModule, RouterLink, DirectionBadgeComponent],
  selector: 'expense-card',
  templateUrl: './expense-card.component.html',
  styleUrls: ['./expense-card.component.scss']
})
export class ExpenseCardComponent implements OnInit, OnDestroy {

  private _subscriptions: Subscription[] = []
  Object = Object
  countFieldOptions: { value: 'vatSum' | 'netSum' | 'grossSum', text: string }[] = [
    { value: 'netSum', text: 'Wartość netto' },
    { value: 'vatSum', text: 'Kwota VAT' },
    { value: 'grossSum', text: 'Wartość brutto' },
  ]

  selectedFieldId = this.countFieldOptions[2]
  range = 12
  data: MonthYearExpenses[] = []
  visibleData: MonthYearExpenses[] = []
  recordsPerPage = 12
  currentPage = 1
  hasMoreRecords = true
  docsCount = 0

  constructor(
    private _documentsService: DocumentsService,
    private _dataSynchronizerService: DataSynchronizerService
  ) {
    this._initialize()
  }

  public toDecimal(value) {
    return Utils.toDecimal(value)
  }

  /**
   * Pobiera prawidłową wartość dokumentu kosztowego
   * Dla dokumentów KSeF używa grossSum z dokumentu (zapisane przy synchronizacji)
   */
  public getDocumentValue(document: DbDocument): number {
    // Dla dokumentów KSeF - zawsze używaj wartości z dokumentu (nie ma pełnych products)
    if (document.source === 'ksef') {
      if (this.selectedFieldId.value === 'grossSum') {
        return document.grossSum || 0;
      }
      // Dla netSum/vatSum - spróbuj wyciągnąć z ksefRawData
      const rawData = (document as any).ksefRawData;
      if (rawData?.Fa) {
        if (this.selectedFieldId.value === 'netSum') {
          return this._extractKsefNetSum(rawData.Fa);
        } else if (this.selectedFieldId.value === 'vatSum') {
          return this._extractKsefVatSum(rawData.Fa);
        }
      }
      // Fallback - oblicz proporcjonalnie (zakładając dominującą stawkę 23% VAT)
      if (this.selectedFieldId.value === 'netSum') {
        return (document.grossSum || 0) / 1.23;
      } else if (this.selectedFieldId.value === 'vatSum') {
        return (document.grossSum || 0) - (document.grossSum || 0) / 1.23;
      }
      return document.grossSum || 0;
    }

    // Dla zwykłych dokumentów - oblicz z produktów
    return this._calculateFromProducts(document);
  }

  /**
   * Wyciąga sumę netto z danych KSeF (P_13_1 do P_13_11)
   */
  private _extractKsefNetSum(fa: any): number {
    let sum = 0;
    for (let i = 1; i <= 11; i++) {
      const key = `P_13_${i}`;
      if (fa[key]) sum += parseFloat(fa[key]) || 0;
    }
    return sum;
  }

  /**
   * Wyciąga sumę VAT z danych KSeF (P_14_1 do P_14_5)
   */
  private _extractKsefVatSum(fa: any): number {
    let sum = 0;
    for (let i = 1; i <= 5; i++) {
      const key = `P_14_${i}`;
      if (fa[key]) sum += parseFloat(fa[key]) || 0;
    }
    return sum;
  }

  /**
   * Oblicza wartość z produktów dokumentu
   */
  private _calculateFromProducts(document: DbDocument): number {
    if (!document.products || document.products.length === 0) {
      return document.grossSum || 0;
    }

    let sum = 0;
    document.products.forEach(product => {
      if (this.selectedFieldId.value === 'grossSum') {
        sum += product.grossValue || 0;
      } else if (this.selectedFieldId.value === 'netSum') {
        sum += product.netValue || 0;
      } else if (this.selectedFieldId.value === 'vatSum') {
        sum += product.amountOfVat || 0;
      }
    });
    return sum;
  }

  /**
   * Pobiera nazwę typu dokumentu do wyświetlenia
   */
  public getDocumentTypeName(document: DbDocument): string {
    if (!document.documentType) return 'Koszt';
    const template = TemplateUtils.GetDocumentById(document.documentType);
    return template?.name || document.documentType;
  }

  /**
   * Pobiera nazwę sprzedawcy (dla kosztów to jest dostawca)
   */
  public getSellerName(document: DbDocument): string {
    if (!document.seller) return 'Brak sprzedawcy';
    // Dla KSeF - może być tylko name
    if (typeof document.seller === 'object' && document.seller.name) {
      return document.seller.name;
    }
    return 'Brak sprzedawcy';
  }

  /**
   * Pobiera walutę dokumentu - dla KSeF może być w różnych miejscach
   */
  public getDocumentCurrency(document: DbDocument): string {
    // Najpierw sprawdź bezpośrednio na dokumencie
    if (document.currency) {
      return document.currency;
    }
    // Dla KSeF sprawdź w ksefRawData
    const rawData = (document as any).ksefRawData;
    if (rawData?.currency) {
      return rawData.currency;
    }
    if (rawData?.Fa?.KodWaluty) {
      return rawData.Fa.KodWaluty;
    }
    return 'PLN';
  }

  public loadMore() {
    this.currentPage++;
    const startIndex = (this.currentPage - 1) * this.recordsPerPage;
    const endIndex = this.currentPage * this.recordsPerPage;
    const newRecords = this.data.slice(startIndex, endIndex);
    this.visibleData = [...this.visibleData, ...newRecords];
    this._hasMoreRecords()
  }

  public changeOption(field) {
    this.selectedFieldId = field
    this._initialize()
  }

  private _initialize() {
    this._buildRange()
    this._addDocuments()
    this._updateVisibleData()
    this._hasMoreRecords()
  }

  private _hasMoreRecords() {
    this.hasMoreRecords = this.currentPage * this.recordsPerPage < this.data.length;
  }

  private _updateVisibleData() {
    const startIndex = (this.currentPage - 1) * this.recordsPerPage;
    const endIndex = this.currentPage * this.recordsPerPage;
    this.visibleData = this.data.slice(startIndex, endIndex);
  }

  private _buildRange() {
    this.data = [];
    const currentDate: DateTime = DateTime.now()
    let months = 12

    const firstDocument = this._documentsService.getOldestExpenseDocument();
    if (firstDocument) {
      const firstDocumentDate = DateTime.fromISO(firstDocument.issueDate);
      months = Math.ceil(currentDate.diff(firstDocumentDate, 'months').months);
      if (months < 12) months = 12;
    }

    let currentDatePointer = currentDate.startOf('month');

    for (let i = 0; i <= months; i++) {
      this.data.push(
        new MonthYearExpenses({
          month: currentDatePointer.month,
          year: currentDatePointer.year,
          countFieldId: this.selectedFieldId.value,
          getDocumentValue: (doc) => this.getDocumentValue(doc),
          getDocumentCurrency: (doc) => this.getDocumentCurrency(doc)
        })
      );
      currentDatePointer = currentDatePointer.minus({ months: 1 });
    }

    if (this.data[0]) this.data[0].expanded = true
  }

  private _addDocuments() {
    this.data = this.data.filter((element: MonthYearExpenses) => {
      // Pobierz wszystkie dokumenty z danego miesiąca
      const allDocuments = this._documentsService.getAllDocumentsByMonthAndYear(element.month, element.year);

      // Filtruj tylko dokumenty z direction = 'expense' (koszty)
      const expenseDocuments = allDocuments.filter((document: DbDocument) =>
        DocumentHelper.getDirection(document) === 'expense'
      );

      if (expenseDocuments.length > 0) {
        expenseDocuments.forEach((document: DbDocument) => {
          element.addDocument(document);
        });

        element.documents.sort((a: DbDocument, b: DbDocument) => {
          const dateA = new Date(a.issueDate);
          const dateB = new Date(b.issueDate);

          if (dateA.getFullYear() !== dateB.getFullYear()) {
            return dateB.getFullYear() - dateA.getFullYear();
          }

          if (dateA.getMonth() !== dateB.getMonth()) {
            return dateB.getMonth() - dateA.getMonth();
          }

          return parseInt(b.documentNo?.toString() || '0') - parseInt(a.documentNo?.toString() || '0');
        });

        return true;
      }

      return false;
    });
  }

  ngOnInit(): void {
    this._subscriptions.push(
      this._dataSynchronizerService.currentData.subscribe({
        next: (data) => {
          if (!data) return
          if (data.table == 'documents') {
            this._initialize()
          }
        }
      }))
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe())
  }

}

export class CurrencySummary {
  currency: string
  summary: number
}

/**
 * Klasa grupująca dokumenty kosztowe wg miesiąca/roku
 */
export class MonthYearExpenses {

  day: number | undefined
  month: number
  monthName: string
  year: number
  documents: DbDocument[] = []
  expanded: boolean = false
  sum = {}
  summary: CurrencySummary[] = []
  countFieldId: 'vatSum' | 'netSum' | 'grossSum' = 'grossSum'
  private _getDocumentValue: (doc: DbDocument) => number
  private _getDocumentCurrency: (doc: DbDocument) => string

  constructor({ day, month, year, countFieldId, getDocumentValue, getDocumentCurrency }: {
    day?: number,
    month: number,
    year: number,
    countFieldId: 'vatSum' | 'netSum' | 'grossSum',
    getDocumentValue: (doc: DbDocument) => number,
    getDocumentCurrency: (doc: DbDocument) => string
  }) {
    this.day = day
    this.month = month
    this.monthName = DateTime.fromObject({ year: year, month: month }).setLocale('pl').toFormat('LLLL')
    this.year = year
    this.countFieldId = countFieldId
    this._getDocumentValue = getDocumentValue
    this._getDocumentCurrency = getDocumentCurrency
  }

  public toggleExpand() {
    this.expanded = !this.expanded
  }

  public addDocument(document: DbDocument) {
    this.documents.push(document)
    this._count()
  }

  public setCountField(fieldId: 'vatSum' | 'netSum' | 'grossSum') {
    this.countFieldId = fieldId
    this._count()
  }

  private _count() {
    this.sum = {}
    if (this.documents.length == 0) return

    this.documents.forEach((document: DbDocument) => {
      const value = this._getDocumentValue(document);
      const currency = this._getDocumentCurrency(document);

      if (typeof this.sum[currency] == 'undefined') this.sum[currency] = 0
      this.sum[currency] += value || 0
    })

    this.summary = Object.keys(this.sum).map(key => { return { currency: key, summary: this.sum[key] } })
  }
}
